import type { AliasPill } from './alias-pills';

/** What a table cell shows for a record with no tags, as its other empty cells do. */
export const EMPTY_TAGS_TEXT = '--';

/**
 * Pills for a list row, which carries alias names only. A record's alias names are unique, so the name
 * keys each pill; the id AliasPills wants is only used by the dismissible kind, which a list never is.
 * @param {readonly string[] | null | undefined} names - The row's alias names, as the list payload sends them.
 * @return {AliasPill[]} One pill per name, in the order given.
 */
export const aliasPillsFromNames = (
  names: readonly string[] | null | undefined
): AliasPill[] => (names ?? []).map((name, index) => ({ id: index, name }));

interface AliasedRecord {
  id: number;
  name: string;
  aliases?: string[];
  aliasesText?: string;
}

/**
 * A picker item for a record from a search result: its id and name as before, and the aliases the row
 * carried, so the option can show them and a typed query can match them.
 * @param {AliasedRecord} record - A development area search result row.
 * @return {object} The item a FreeTextComboBox takes.
 */
export const aliasedOption = (record: AliasedRecord) => ({
  id: record.id,
  value: record.name,
  label: record.name,
  aliases: record.aliases ?? [],
  aliasesText: record.aliasesText ?? '',
});

/**
 * What AliasOption needs, read from a picker item built by `aliasedOption`.
 * @param {Record<string, unknown>} item - A picker item.
 * @return {{ label: string, aliases: string[] }} The option's name and aliases.
 */
export const aliasOptionProps = (item: Record<string, unknown>) => ({
  label: typeof item.label === 'string' ? item.label : '',
  aliases: Array.isArray(item.aliases)
    ? item.aliases.filter((alias): alias is string => typeof alias === 'string')
    : [],
});

/**
 * The text a typed query may also match on a picker item: its aliases. The server search already matched
 * them; without this the combobox's own narrowing would drop an item whose name lacks what was typed.
 * @param {Record<string, unknown>} item - A picker item built by `aliasedOption`.
 * @return {string} The item's aliases, joined.
 */
export const aliasedSearchText = (item: Record<string, unknown>): string =>
  typeof item.aliasesText === 'string' ? item.aliasesText : '';
